import { memo } from 'react';
import { useSelector } from 'react-redux';

import { ViewTypeName } from '@air/api/types';
import { useBreakpointsContext } from '@air/provider-media-query';

import { MobileToggleNestedItemsButton } from '~/_components/MobileToggleNestedItemsButton';
import { ViewSortingMenu } from '~/components/ViewControls/ViewSortingMenu/ViewSortingMenu';
import { useClearSearchSortOverrideWhenQueryCleared } from '~/hooks/search/useClearSearchSortOverrideWhenQueryCleared';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { currentViewTypeNameSelector } from '~/store/configViews/selectors';

export type ShowcaseHeaderViewControlsProps = {
  visibilityToggleMenu: React.ReactNode;
  /**
   * When true, the expand-nested-content button is hidden. Only the public
   * collection path passes this — `useShortIdContext()` can't be called here
   * because this component is also rendered inside `PrivateShowcaseHeader`,
   * which has no `ShortIdProvider` ancestor and would throw at runtime.
   */
  isExpandNestedDisabled?: boolean;
};

export const ShowcaseHeaderViewControls = memo(
  ({ visibilityToggleMenu, isExpandNestedDisabled = false }: ShowcaseHeaderViewControlsProps) => {
    useClearSearchSortOverrideWhenQueryCleared();
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);
    const inGalleryView = currentViewTypeName === ViewTypeName.gallery;
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { ui2026 } = useTemporaryFeatureFlags();
    const showVisibilityToggleMenu = isAboveMediumScreen || ui2026;
    const showMobileExpandNestedButton = !ui2026 && !isAboveMediumScreen && !isExpandNestedDisabled;

    return (
      <div className="flex items-center gap-2">
        {isAboveMediumScreen && inGalleryView && <ViewSortingMenu />}
        {showMobileExpandNestedButton && <MobileToggleNestedItemsButton />}
        {showVisibilityToggleMenu && visibilityToggleMenu}
        {/* TODO: Add ViewTypeToggleMenu when further views are implemented
      - Table View (https://linear.app/air/issue/REV-877/public-collection-landing-page-add-support-for-table-view)
      - Kanban View (https://linear.app/air/issue/REV-878/public-collection-landing-page-add-support-for-kanban-view) */}
      </div>
    );
  },
);

ShowcaseHeaderViewControls.displayName = 'ShowcaseHeaderViewControls';
